<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCommercialGoalFormat } from './useCommercialGoalFormat';

import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import GoalProgressBar from './GoalProgressBar.vue';

const props = defineProps({
  goal: { type: Object, required: true },
});

const emit = defineEmits(['edit', 'toggleStatus', 'delete']);

const { t } = useI18n();
const { formatValue, formatType, formatPeriod } = useCommercialGoalFormat();

const isActive = computed(() => props.goal.status === 'active');

const SITUATION_COLORS = {
  current: 'teal',
  scheduled: 'blue',
  ended: 'amber',
  inactive: 'slate',
};
const situation = computed(() =>
  isActive.value ? props.goal.situation : 'inactive'
);

const scopeLabel = computed(() =>
  props.goal.scopeType === 'team'
    ? t('COMMERCIAL_GOALS.SCOPE.TEAM')
    : props.goal.assignedUser?.name || t('COMMERCIAL_GOALS.SCOPE.USER')
);

const valueSummary = computed(() => {
  const { goalType, progress } = props.goal;
  if (progress.state === 'no_data') return '';
  return t('COMMERCIAL_GOALS.ROW.CURRENT_OF_TARGET', {
    current: formatValue(goalType, progress.currentValue),
    target: formatValue(goalType, progress.targetValue),
  });
});
</script>

<template>
  <div
    class="grid items-center gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_auto]"
  >
    <div class="flex flex-col gap-1 min-w-0">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ goal.title }}
        </span>
        <Label
          compact
          :label="t(`COMMERCIAL_GOALS.SITUATION.${situation.toUpperCase()}`)"
          :color="SITUATION_COLORS[situation]"
        />
        <Label
          v-if="goal.userRemoved"
          compact
          color="ruby"
          :label="t('COMMERCIAL_GOALS.SCOPE.USER_REMOVED')"
        />
      </div>
      <span class="text-xs text-n-slate-11">
        {{ formatType(goal) }}
        · {{ scopeLabel }} · {{ formatPeriod(goal) }}
      </span>
    </div>

    <div class="flex flex-col gap-1.5 min-w-0">
      <span class="text-xs text-n-slate-11">
        {{ valueSummary }}
      </span>
      <GoalProgressBar
        :percent="goal.progress.progressPercent"
        :state="goal.progress.state"
      />
    </div>

    <div class="flex gap-1">
      <Button
        v-tooltip.top="t('COMMERCIAL_GOALS.ACTIONS.EDIT')"
        icon="i-lucide-pencil"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('edit', goal)"
      />
      <Button
        v-tooltip.top="
          isActive
            ? t('COMMERCIAL_GOALS.ACTIONS.DEACTIVATE')
            : t('COMMERCIAL_GOALS.ACTIONS.REACTIVATE')
        "
        :icon="isActive ? 'i-lucide-power-off' : 'i-lucide-power'"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('toggleStatus', goal)"
      />
      <Button
        v-tooltip.top="t('COMMERCIAL_GOALS.ACTIONS.DELETE')"
        icon="i-lucide-trash-2"
        variant="ghost"
        color="ruby"
        size="sm"
        @click="emit('delete', goal)"
      />
    </div>
  </div>
</template>
